import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";
import { useAlert } from "./useAlert";

interface IUserInfo {
  user_settings: {
    notifications?: boolean;
    payment_method?: "community" | "personal";
    community_id?: string;
  };
  house_hold?: number;
}
export interface IUser {
  _id?: string;
  userId?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_role?: string | number;
  createdAt?: string;
  updatedAt?: string;
  balance?: number;
  totalRevenue?: number;
  location?: string;
  phone_number?: string;
  profileImage?: string;
  pending_delete?: boolean;
  deletion_date?: string;
  isBlocked?: boolean;
  qrCode?: string;
  platform?: string;
  amountDonated?: number;
  postal_code?: string;
  user_info?: IUserInfo;
  dropsVolume?: number;
  tomraDropsVolume?: number;
  beevisionDropsVolume?: number;
  defaultLocation?: string;
  leading_source?: {
    _id?: string;
    name?: string;
  };
  kMeans?: number | null;
  invitedBy?: string;
}

interface IUserPlatformStats {
  iosUsers: number;
  androidUsers: number;
  unknownUsers?: number;
}

interface IUserStats {
  totalCO2Saved: number;
  totalDropsWeight: {
    total: number;
  }[];
  totalWithdrawed: number;
}

interface IStore {
  /** State */
  allUsers: IUser[];
  allCommunityUsers: IUser[];
  usersCount: number;
  userStats: IUserStats;
  userPlatformStats: IUserPlatformStats;
  hasNextPage: boolean;
  oneUser: IUser | null;
  updatedUser: string | null;
  searchText: string;
  allUsersLoading: boolean;
  pendingDeletionUsers: IUser[];
  pendingDeletionLoading: boolean;
  hasNextPendingDeletionPage: boolean;
  /** Callbacks */
  getUsersCount: (
    droppedMaterials: string[],
    nonDroppedMaterials: string[],
    usersWithoutDropInLast5Days: boolean,
    allAdmins?: boolean,
    usersWithoutPostcode?: boolean,
    selectedLocations?: string[],
    superAdminsOnly?: boolean
  ) => void;
  addBalance: (userId: string, amount: number) => void;
  getUserStats: (userId: string) => void;
  getUserPlatformStats: () => void;
  updateUserInfo: (userId: string, userInfo: IUserInfo) => void;
  downloadUsersInCSV: () => void;
  getAllUsers: (
    page?: number,
    limit?: number,
    search?: string,
    filters?: any,
    sort?: any,
    postalCodeNullOnly?: boolean,
    showUsersWithoutDropsInLast5Days?: boolean,
    droppedMaterials?: string[],
    showUsersWithoutDropCount?: boolean,
    showUsersWithOnePlusDrops?: boolean,
    showUsersWithTwoPlusDrops?: boolean,
    showUsersWithThreePlusDrops?: boolean
  ) => void;
  getUsersByCommunity: (communityId: string) => void;
  getOneUser: (id: string) => void;
  getPendingDeletionUsers: (page?: number, limit?: number) => void;
  updateUserRole: (id: string, user_role: string) => void;
  updateUser: (id: string, user: IUser) => void;
  blockUser: (id: string) => void;
  unblockUser: (id: string) => void;
  setSearchText: (text: string) => void;
  /** Clear callbacks */
  clearUpdatedUser: () => void;
  clearSearchText: () => void;
}

export const useUsers = create<IStore>()((set) => ({
  /** State */
  allUsers: [],
  usersCount: 0,
  allCommunityUsers: [],
  userStats: {
    totalCO2Saved: 0,
    totalDropsWeight: [],
    totalWithdrawed: 0,
  },
  userPlatformStats: {
    iosUsers: 0,
    androidUsers: 0,
  },
  hasNextPage: false,
  oneUser: null,
  updatedUser: null,
  searchText: "",
  allUsersLoading: false,
  pendingDeletionUsers: [],
  pendingDeletionLoading: false,
  hasNextPendingDeletionPage: false,
  /** Callbacks */
  getUsersCount: async (
    droppedMaterials = [],
    nonDroppedMaterials = [],
    usersWithoutDropInLast5Days = false,
    allAdmins = false,
    usersWithoutPostcode = false,
    selectedLocations = [],
    superAdminsOnly = false,
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`users/count`, {
        params: {
          droppedMaterials,
          nonDroppedMaterials,
          usersWithoutDropInLast5Days,
          allAdmins,
          usersWithoutPostcode,
          selectedLocations,
          superAdminsOnly,
        },
      });
      if (response?.data || response?.data === 0) {
        set({ usersCount: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching users count", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addBalance: async (userId: string, amount: number) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(`users/add-balance/${userId}`, {
        amount,
      });
      if (response?.data) {
        set({
          oneUser: response.data,
        });
        useAlert.getState().setAlert({
          isOpen: true,
          message: "Balance added successfully!",
          severity: "success",
        });
      }
    } catch (error: any) {
      LOG.error("Error on adding balance", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message ||
          "Failed to add balance. Please try again.",
        severity: "error",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getUserStats: async (userId: string) => {
    try {
      const response = await axios.get(`users/stats/${userId}`);
      if (response?.data) {
        set({ userStats: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching user stats", { error });
    }
  },
  getUserPlatformStats: async () => {
    try {
      const response = await axios.get(`users/platform-stats`);
      if (response?.data) {
        set({ userPlatformStats: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching user platform stats", { error });
    }
  },
  updateUserInfo: async (userId, userInfo) => {
    try {
      const response = await axios.post(`/users/userInfo/${userId}`, userInfo);
      if (response?.data?.user) {
        set({
          oneUser: response?.data?.user,
        });
      }
    } catch (error) {
      LOG.error("Error on updateUserInfo", { error });
    }
  },
  downloadUsersInCSV: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`users/csv`);
      if (response?.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "users.csv");
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      LOG.error("Error on downloading users in CSV", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getUsersByCommunity: async (communityId: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`users/all/community/${communityId}`);
      if (response?.data) {
        set({ allCommunityUsers: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching users by community", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAllUsers: async (
    page = 1,
    limit = 10,
    search: any,
    filters = {},
    sort = { createdAt: "desc" },
    postalCodeNullOnly = false,
    usersWithoutDropInLast5Days = false,
    droppedMaterials = [],
    showUsersWithoutDropCount,
    showUsersWithOnePlusDrops,
    showUsersWithTwoPlusDrops,
    showUsersWithThreePlusDrops
  ) => {
    useAppLoading.getState().setIsLoading(true);
    set({ allUsersLoading: true });
    try {
      const response = await axios.get(
        `users/all?page=${page}&limit=${limit}&search=${search}`,
        {
          params: {
            filters,
            sort,
            postalCodeNullOnly,
            usersWithoutDropInLast5Days,
            droppedMaterials,
            showUsersWithoutDropCount,
            showUsersWithOnePlusDrops,
            showUsersWithTwoPlusDrops,
            showUsersWithThreePlusDrops
          },
        }
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          allUsers:
            page === 1
              ? response?.data
              : [...state.allUsers, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getching all users", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      set({ allUsersLoading: false });
    }
  },
  getOneUser: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`users/${id}`);
      if (response?.data) {
        set({ oneUser: response.data });
      }
    } catch (error) {
      LOG.error("Error on getching one user", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getPendingDeletionUsers: async (page = 1, limit = 20) => {
    set({ pendingDeletionLoading: true });
    try {
      const response = await axios.get(
        `users/pending-deletion?page=${page}&limit=${limit}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPendingDeletionPage: response?.data?.length === limit,
          pendingDeletionUsers:
            page === 1
              ? response?.data
              : [...state.pendingDeletionUsers, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getching pending deletion users", { error });
    } finally {
      set({ pendingDeletionLoading: false });
    }
  },
  updateUser: async (id, user) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`users/${id}`, user);
      if (response?.data) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: "You have successfully updated user!",
          severity: "success",
        });
        set({
          oneUser: response.data?.user,
          updatedUser: response?.data?.user?._id,
        });
      }
    } catch (error) {
      LOG.error("Error on updating user role", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  blockUser: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(`users/block/${id}`);
      if (response?.data) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: "You have successfully blocked user!",
          severity: "success",
        });
        set((state) => ({
          oneUser: response.data,
          allUsers: state.allUsers.map((u) =>
            u._id === id ? { ...u, isBlocked: true } : u
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on blocking user", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        message: "Error on blocking user!",
        severity: "error",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  unblockUser: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(`users/unblock/${id}`);
      if (response?.data) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: "You have successfully unblocked user!",
          severity: "success",
        });
        set((state) => ({
          oneUser: response.data,
          allUsers: state.allUsers.map((u) =>
            u._id === id ? { ...u, isBlocked: false } : u
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on unblocking user", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        message: "Error on unblocking user!",
        severity: "error",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateUserRole: async (id: string, user_role: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`users/${id}`, {
        user_role,
      });
      if (response?.data) {
        set({ oneUser: response.data, updatedUser: response?.data?.user?._id });
      }
    } catch (error) {
      LOG.error("Error on updating user role", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  setSearchText: (text) => set({ searchText: text }),
  /** Clear callbacks */
  clearSearchText: () => set({ searchText: "" }),
  clearUpdatedUser: () => set({ updatedUser: null }),
}));
